import React, { FC, useEffect } from 'react';
import {
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Text,
  useTheme,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import { useQuery } from 'react-query';
import FormikInput from 'components/primitives/FormikInput';
import { fetchQALog } from 'api/QALog';
import {
  initialValues,
  QA_LOG_LEFTOVERS_QUERY_KEY,
  sumLeftoverQuantitiesBySku,
  validationSchema,
} from './AddQALogModal.utils';
import {
  AddQALogModalViewProps,
  FormValues,
  QAItem,
} from './AddQALogModal.props';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import SkuMultiSelect from 'components/primitives/SkuMultiSelect';
import { SKU } from 'types/sku';
import FormikTextArea from 'components/primitives/FormikTextArea';
import FormikCheckBox from 'components/primitives/FormikCheckBox';
import { EmployeeFilterableSelect } from '../EmployeeSelect';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
  noOfLines: 1,
  marginBottom: '4px',
  marginLeft: '16px',
  fontWeight: '500',
};

const labelTextareaProps = {
  fontSize: '16px',
  color: 'support.500',
  noOfLines: 1,
  marginBottom: '4px',
  fontWeight: '500',
};

const css = {
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    width: '10px',
    height: '10px',
    background: 'primary.900',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'primary.400',
    borderRadius: '24px',
  },
};

const LeftoverPackagesSection: FC<{
  washHubId: string;
  skus: SKU[];
}> = ({ washHubId, skus }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { data, isLoading } = useQuery(
    [QA_LOG_LEFTOVERS_QUERY_KEY, washHubId],
    () =>
      fetchQALog({
        washHub: washHubId,
        page: 1,
        limit: 500,
      }),
    {
      enabled: Boolean(values.addToPackages && washHubId),
    },
  );

  useEffect(() => {
    if (!values.addToPackages && values.combineLeftovers) {
      setFieldValue('combineLeftovers', false);
    }
  }, [values.addToPackages, values.combineLeftovers, setFieldValue]);

  if (!values.addToPackages) {
    return null;
  }

  const leftoverBySku = sumLeftoverQuantitiesBySku(data?.items || []);
  const leftoverEntries = Object.entries(leftoverBySku);

  return (
    <Flex mt={4} direction="column" align="center" gap={3}>
      {isLoading ? (
        <Text fontSize="14px" color="support.500">
          Loading leftover quantities from previous QA logs...
        </Text>
      ) : leftoverEntries.length === 0 ? (
        <Text fontSize="14px" color="support.500">
          No leftover passed quantities from previous QA logs.
        </Text>
      ) : (
        <Flex direction="column" gap={1} minW="280px">
          <Text fontSize="14px" fontWeight="600" color="support.500">
            Leftover passed quantities
          </Text>
          {leftoverEntries.map(([skuId, quantity]) => {
            const skuName =
              skus.find((sku) => sku._id === skuId)?.name || skuId;

            return (
              <Flex key={skuId} justify="space-between" gap={4}>
                <Text fontSize="14px" color="support.500">
                  {skuName}
                </Text>
                <Text fontSize="14px" color="support.500">
                  {quantity}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      )}
      {leftoverEntries.length > 0 && (
        <FormikCheckBox
          field="combineLeftovers"
          name="Combine the passed amount with the left over quantity from previous QA logs"
        />
      )}
    </Flex>
  );
};

const AddQALogModalView: FC<AddQALogModalViewProps> = (props) => {
  const {
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    isLoadingSKU,
    skus,
    selectedSkus,
    setSelectedSkus,
    washHubId,
  } = props;

  const theme = useTheme();
  const primaryColor = theme?.colors?.primary['800'];

  const handleSkuChange = (
    selectedSkus: string[],
    setFieldValue: any,
    currentItems: QAItem[],
  ) => {
    setSelectedSkus(selectedSkus);

    const currentItemsMap = currentItems.reduce((acc, item) => {
      acc[item.sku] = item;
      return acc;
    }, {} as Record<string, QAItem>);

    const newItems: QAItem[] = selectedSkus.map((sku) => {
      const existingItem = currentItemsMap[sku] || {};
      return {
        sku,
        passed: existingItem.passed || undefined,
        dirty: existingItem.dirty || undefined,
        damaged: existingItem.damaged || undefined,
        total: existingItem.total || undefined,
        yield: existingItem.yield || undefined,
      };
    });

    setFieldValue('items', newItems);
  };

  const handleClose = () => {
    setSelectedSkus([]); // Clear selected SKUs
    onClose(); // Call the provided onClose handler
  };

  return (
    <Modal
      size="6xl"
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="500">
          QA Log creation
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" px={6} pb={6}>
            <Formik
              onSubmit={onSubmit}
              initialValues={initialValues}
              validationSchema={validationSchema}>
              {({ values, setFieldValue }: FormikProps<FormValues>) => {
                const totals = values?.items?.reduce(
                  (acc, item) => {
                    const passed = Number(item?.passed || 0);
                    const dirty = Number(item?.dirty || 0);
                    const damaged = Number(item?.damaged || 0);

                    acc.passed += passed;
                    acc.dirty += dirty;
                    acc.damaged += damaged;
                    acc.total += passed + dirty + damaged;

                    return acc;
                  },
                  { passed: 0, dirty: 0, damaged: 0, total: 0 }, // Initial accumulator
                );
                return (
                  <Form>
                    <Flex direction="column">
                      <Flex gap={4}>
                        <Flex flex={1}>
                          <EmployeeFilterableSelect
                            containerProps={{ flex: 1, mt: 3 }}
                            label="Employee"
                            name="employee"
                            labelProps={labelProps}
                          />
                        </Flex>
                        <Flex flex={1} gap={4}>
                          <Flex>
                            <FormikDatepicker
                              name="date"
                              label="Date Selection"
                              labelProps={labelProps}
                              formatInUTC
                            />
                          </Flex>
                          <Flex>
                            <FormikInput
                              name="hours"
                              label="Hours Worked"
                              labelProps={labelProps}
                              containerProps={{ flex: 1 }}
                            />
                          </Flex>
                        </Flex>
                        <Flex flex={1}>
                          <SkuMultiSelect
                            label="Select Items SKU’s"
                            labelProps={labelProps}
                            values={selectedSkus}
                            onChange={(newSelectedSkus) =>
                              handleSkuChange(
                                newSelectedSkus,
                                setFieldValue,
                                values.items,
                              )
                            }
                            isLoadingSKU={isLoadingSKU}
                            skus={skus}
                          />
                        </Flex>
                      </Flex>

                      <Flex direction="column" pos="relative" mt={8}>
                        <Flex w="100%" overflow="auto" css={css}>
                          <Flex
                            overflow="auto"
                            w="100vw"
                            direction="column"
                            fontSize="13px">
                            <Flex
                              {...{
                                position: 'sticky',
                                top: '0',
                                w: '100%',
                                backdropFilter: 'blur(15px)',
                              }}
                              gap="5px"
                              px={6}
                              py={4}
                              flex={1}
                              bg={`${primaryColor}80`}
                              color="support.500"
                              userSelect="none"
                              fontWeight="bold"
                              borderRadius="5px">
                              <>
                                <Text flex={1.5}>Item SKU</Text>
                                <Text flex={1}>Passed</Text>
                                <Text flex={1}>Dirty</Text>
                                <Text flex={1}>Damaged</Text>
                                <Text flex={1}>Total</Text>
                                <Text flex={1}>Yield</Text>
                              </>
                            </Flex>
                            {values?.items?.length > 0 ? (
                              <>
                                {values?.items?.map(
                                  (item: QAItem, i: number) => {
                                    const skuName = skus.find(
                                      (sku: SKU) => sku?._id === item?.sku,
                                    )?.name;
                                    const total = Number(
                                      Number(item?.damaged || 0) +
                                        Number(item?.dirty || 0) +
                                        Number(item?.passed || 0) || 0,
                                    );
                                    const yieldValue =
                                      total > 0
                                        ? (
                                            ((item.passed || 0) / total) *
                                            100
                                          ).toFixed(2)
                                        : 0;

                                    const updateTotalAndYield = (
                                      index: number,
                                      passed: number,
                                      dirty: number,
                                      damaged: number,
                                      values: FormValues,
                                      setFieldValue: any,
                                    ) => {
                                      const total = passed + dirty + damaged;
                                      const yieldValue =
                                        total > 0 ? (passed / total) * 100 : 0;

                                      setFieldValue(
                                        `items[${index}].total`,
                                        total,
                                      );
                                      setFieldValue(
                                        `items[${index}].yield`,
                                        yieldValue,
                                      );
                                    };

                                    return (
                                      <Flex
                                        gap="5px"
                                        px={6}
                                        py={2}
                                        flex={1}
                                        bg={`${primaryColor}30`}
                                        color="support.500"
                                        userSelect="none">
                                        <>
                                          <Text flex={1.5}>{skuName}</Text>
                                          <FormikInput
                                            type="number"
                                            name={`items[${i}].passed`}
                                            labelProps={labelProps}
                                            containerProps={{
                                              flex: 1,
                                              margin: 1,
                                            }}
                                            onInput={(
                                              e: React.ChangeEvent<HTMLInputElement>,
                                            ) => {
                                              const passedValue = Number(
                                                e.target.value || 0,
                                              );
                                              setFieldValue(
                                                `items[${i}].passed`,
                                                passedValue,
                                              );
                                              const dirtyValue = Number(
                                                values.items[i]?.dirty || 0,
                                              );
                                              const damagedValue = Number(
                                                values.items[i]?.damaged || 0,
                                              );
                                              updateTotalAndYield(
                                                i,
                                                passedValue,
                                                dirtyValue,
                                                damagedValue,
                                                values,
                                                setFieldValue,
                                              );
                                            }}
                                          />
                                          <FormikInput
                                            type="number"
                                            name={`items[${i}].dirty`}
                                            labelProps={labelProps}
                                            containerProps={{
                                              flex: 1,
                                              margin: 1,
                                            }}
                                            onInput={(
                                              e: React.ChangeEvent<HTMLInputElement>,
                                            ) => {
                                              const dirtyValue = Number(
                                                e.target.value || 0,
                                              );
                                              setFieldValue(
                                                `items[${i}].dirty`,
                                                dirtyValue,
                                              );
                                              const passedValue = Number(
                                                values.items[i]?.passed || 0,
                                              );
                                              const damagedValue = Number(
                                                values.items[i]?.damaged || 0,
                                              );
                                              updateTotalAndYield(
                                                i,
                                                passedValue,
                                                dirtyValue,
                                                damagedValue,
                                                values,
                                                setFieldValue,
                                              );
                                            }}
                                          />
                                          <FormikInput
                                            type="number"
                                            name={`items[${i}].damaged`}
                                            labelProps={labelProps}
                                            containerProps={{
                                              flex: 1,
                                              margin: 1,
                                            }}
                                            onInput={(
                                              e: React.ChangeEvent<HTMLInputElement>,
                                            ) => {
                                              const damagedValue = Number(
                                                e.target.value || 0,
                                              );
                                              setFieldValue(
                                                `items[${i}].damaged`,
                                                damagedValue,
                                              );
                                              const passedValue = Number(
                                                values.items[i]?.passed || 0,
                                              );
                                              const dirtyValue = Number(
                                                values.items[i]?.dirty || 0,
                                              );
                                              updateTotalAndYield(
                                                i,
                                                passedValue,
                                                dirtyValue,
                                                damagedValue,
                                                values,
                                                setFieldValue,
                                              );
                                            }}
                                          />
                                          <FormikInput
                                            type="text"
                                            isReadOnly
                                            name={`items[${i}].total`}
                                            labelProps={labelProps}
                                            value={String(total)}
                                            containerProps={{
                                              flex: 1,
                                              margin: 1,
                                            }}
                                          />
                                          <FormikInput
                                            name={`items[${i}].yield`}
                                            value={`${String(yieldValue)}%`}
                                            isReadOnly
                                            labelProps={labelProps}
                                            containerProps={{
                                              flex: 1,
                                              margin: 1,
                                            }}
                                          />
                                        </>
                                      </Flex>
                                    );
                                  },
                                )}
                                {/* Totals Row */}
                                <Flex
                                  gap="5px"
                                  px={6}
                                  py={3}
                                  flex={1}
                                  roundedBottom={4}
                                  bg="green.100"
                                  color="black"
                                  fontWeight="bold"
                                  userSelect="none">
                                  <>
                                    <Text flex={1.5}>Total</Text>
                                    <Text flex={1}>{totals.passed}</Text>
                                    <Text flex={1}>{totals.dirty}</Text>
                                    <Text flex={1}>{totals.damaged}</Text>
                                    <Text flex={1}>{totals.total}</Text>
                                    <Text flex={1}>
                                      {totals.total > 0
                                        ? (
                                            (totals.passed / totals.total) *
                                            100
                                          ).toFixed(2)
                                        : '0.00'}
                                      %
                                    </Text>
                                  </>
                                </Flex>
                              </>
                            ) : (
                              <Text
                                bg={`${primaryColor}30`}
                                flex={1}
                                py={2}
                                my={2}
                                align="center"
                                justifyContent={'center'}>
                                No SKU Selected
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                      </Flex>
                      <Flex mt={3}>
                        <FormikTextArea
                          labelProps={labelTextareaProps}
                          name="notes"
                          label="QA Log Notes"
                          placeholder="Your text here"
                        />
                      </Flex>

                      <Flex mt={4} justifyContent="center">
                        <FormikCheckBox
                          field="addToPackages"
                          name="Automatically add to packages"
                        />
                      </Flex>
                      <LeftoverPackagesSection
                        washHubId={washHubId}
                        skus={skus}
                      />

                      <Flex
                        my={12}
                        flexDirection="row"
                        justifyContent="center"
                        gap={8}>
                        <Button
                          w="200px"
                          onClick={onClose}
                          isDisabled={isLoading}
                          isLoading={isLoading}
                          variant="outline">
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          w="200px"
                          isLoading={isLoading}
                          isDisabled={isLoading}>
                          Submit
                        </Button>
                      </Flex>
                    </Flex>
                  </Form>
                );
              }}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddQALogModalView;
